import CancelIcon from '@eduzz/houston-icons/Cancel';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Collapse,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CoProducerClass from 'classes/CoProducer';
import CustomMaskedInput from 'components/MaskedInput';
import * as React from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { finalize } from 'rxjs/operators';
import { sendEventToGA } from 'v2/helpers/analytics';
import productService from 'v2/services/product';

import { useStyles } from './styles';

type SolidaritySquadModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  productId: number;
};

const PROJECT_ELEVAR_ID = 75879698;
const DEFAULT_COMMISSION_VALUE = 5;
const GA_CATEGORY = 'SS Modal - Projeto Elevar';

enum CloseReason {
  NOT_NOW_BUTTON,
  CLOSE_BUTTON,
}

function SolidaritySquadModal({ isOpen, handleClose: handleCloseProp, productId }: SolidaritySquadModalProps) {
  const classes = useStyles();
  const [updateCoproducers] = useCallbackObservable((productId, coproducers) => {
    setIsAdding(true);
    return productService.updateCoproducers(productId, coproducers).pipe(
      finalize(() => {
        setIsAdding(false);
        toast.success('Doação automática configurada com sucesso');
        handleClose();
      })
    );
  }, []);

  const [commissionValue, setCommissionValue] = React.useState(DEFAULT_COMMISSION_VALUE);
  const [commissionError, setCommissionError] = React.useState('');
  const [isTermsAccepted, setIsTermsAccepted] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);
  const [isAboutDonationsExpanded, setIsAboutDonationsExpanded] = React.useState(false);

  function validateCommissionValue(value: number) {
    const minimumCommissionValue = 0.01;
    if (value < minimumCommissionValue) {
      setCommissionError('O valor de porcentagem tem que ser no mínimo 0,01');
    } else {
      setCommissionError(null);
    }
  }

  function handleCommissionValueChange(value: string) {
    const formattedValue = Number(value.replace(',', '.'));
    validateCommissionValue(formattedValue);
    setCommissionValue(formattedValue);
  }

  function handleTermsChange(e: React.ChangeEvent<HTMLInputElement>) {
    setIsTermsAccepted(e.target.checked);
  }

  function handleCollapse() {
    setIsAboutDonationsExpanded((prev) => !prev);
  }

  function createElevarAsCoProducer() {
    return new CoProducerClass({
      new: true,
      pending: false,
      business_name: 'Instituto Elevar',
      email: 'contato@projetoelevar.com.br',
      comission_type: 'P',
      comission_value: commissionValue,
      customer_id: PROJECT_ELEVAR_ID,
    });
  }

  function removeCommissionFromProducer(coproducers: any[]) {
    return coproducers.map((coproducer) => {
      if (coproducer.is_producer) {
        coproducer.comission_value -= commissionValue;
      }
      return coproducer;
    });
  }

  function handleSubmit(productId: number) {
    productService.getCoproducers(productId).subscribe((coproducers) => {
      const updatedCoproducers = removeCommissionFromProducer(coproducers.active);
      updateCoproducers(productId, [...updatedCoproducers, createElevarAsCoProducer()]);
    });
    sendEventToGA({
      category: GA_CATEGORY,
      action: 'Save Donation',
    });
  }

  function handleClose(closeReason?: CloseReason) {
    handleCloseProp();
    if (closeReason === CloseReason.CLOSE_BUTTON) {
      sendEventToGA({
        category: GA_CATEGORY,
        action: 'Close',
      });
    }
    if (closeReason === CloseReason.NOT_NOW_BUTTON) {
      sendEventToGA({
        category: GA_CATEGORY,
        action: 'Not Now',
      });
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle className={classes.title}>
        Faça parte do Projeto Elevar{' '}
        <IconButton className={classes.closeIconBtn} size='small' aria-label='Fechar modal' onClick={() => handleClose(CloseReason.CLOSE_BUTTON)}>
          <CancelIcon />
        </IconButton>{' '}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <p className={classes.contentText}>Contribua com parte das suas vendas e ajude milhares de famílias. </p>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id='business_name'
              name='business_name'
              label='Projeto Social'
              fullWidth
              disabled
              value='Instituto Elevar'
              InputProps={{
                disableUnderline: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomMaskedInput
              id='comission_value'
              name='comission_value'
              mask='percent'
              label='Valor da contribuição'
              helperText={commissionError || 'Defina a porcentagem sobre as vendas deste produto que você deseja doar'}
              error={!!commissionError}
              startAdornment='%'
              fullWidth
              disableUnderline
              onChange={handleCommissionValueChange}
              value={String(commissionValue)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                min: 0.01,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <h5 className={classes.aboutDonationsTitle} role='button' onClick={handleCollapse} aria-expanded={isAboutDonationsExpanded}>
              Sobre as doações: <ExpandMoreIcon className={isAboutDonationsExpanded ? classes.rotateIcon : ''} />
            </h5>
            <Collapse in={isAboutDonationsExpanded}>
              <p className={classes.aboutDonationsText}>
                O Projeto Elevar foi criado pela Eduzz em 2021 para melhorar a vida de crianças, jovens, mulheres e famílias através da Educação e do
                Empreendedorismo.
              </p>
              <p className={classes.aboutDonationsText}>
                Você pode ajudar a Elevar a vida destas pessoas e da sociedade oferecendo uma pequena parte de suas vendas para o Projeto. A Eduzz vai
                tornar o Instituto seu coprodutor no Produto e sempre que houver uma venda, automaticamente um percentual do valor é creditado para a
                conta do Instituto.
              </p>
              <p className={classes.aboutDonationsText}>
                Todos os anos milhares de vidas são transformadas através do Projeto: da equipe, dos voluntários e da parceria com empreendedores e
                órgãos públicos. Para saber conhecer mais detalhes do Projeto acesse{' '}
                <a href='https://www.projetoelevar.org.br/?utm_source=modal-orbita' target='_blank' rel='noopener noreferrer'>
                  projetoelevar.org.br
                </a>{' '}
                ou pelo insta:{' '}
                <a href='https://www.instagram.com/projetoelevar' target='_blank' rel='noopener noreferrer'>
                  @projetoelevar
                </a>
              </p>
            </Collapse>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={isTermsAccepted} onChange={handleTermsChange} />}
              label='Concordo em doar parte de minhas vendas para o Instituto Elevar.'
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant='text' onClick={() => handleClose(CloseReason.NOT_NOW_BUTTON)}>
          Agora não
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!!commissionError || !isTermsAccepted || isAdding}
          startIcon={isAdding && <CircularProgress size={20} />}
          onClick={() => handleSubmit(productId)}
        >
          Salvar doação
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { SolidaritySquadModal };
